import { DatePipe } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Component, ElementRef, inject, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { TranslocoCurrencyPipe } from '@jsverse/transloco-locale';
import { Q8Breakpoints } from 'core';
import { LoadingSpinner } from 'design-system';
import { catchError, forkJoin, of } from 'rxjs';
import { CustomersNoteBottomSheet } from '../../components/bt-sheet-components/customers-note-bottom-sheet/customers-note-bottom-sheet';
import { CustomersNotificationsBottomSheet } from '../../components/bt-sheet-components/customers-notifications-bottom-sheet/customers-notifications-bottom-sheet';
import { CustomersAddContactDialogComponent } from '../../components/dialog-components/customer-details/customers-add-contact-dialog-component/customers-add-contact-dialog-component';
import { CustomersAddressDetailsDialogComponent } from '../../components/dialog-components/customer-details/customers-address-details-dialog-component/customers-address-details-dialog-component';
import { CustomersDeliveryDestinationsDialogComponent } from '../../components/dialog-components/customer-details/customers-delivery-destinations-dialog-component/customers-delivery-destinations-dialog-component';
import { CustomersEditAddressDetailsDialogComponent } from '../../components/dialog-components/customer-details/customers-edit-address-details-dialog-component/customers-edit-address-details-dialog-component';
import { CustomersNoteDialogComponent } from '../../components/dialog-components/customer-details/customers-note-dialog-component/customers-note-dialog-component';
import { CustomerContact } from '../../interfaces/customer-contacts.model';
import { CustomerNotes } from '../../interfaces/customer-detail-notes.model';
import { CustomerNotificationHeader } from '../../interfaces/customer-notification.model';
import { DeliveryDestinationEditResult, DeliveryDestinationEditValue, DeliveryDestinationNote, DeliveryDestinations, UpdateDeliveryDestinationPayload } from '../../interfaces/deliveryDestionations.model';
import { CustomerDetailNotification, CustomerDetailService } from '../../services/customer-detail.service';

type ActiveDeliveryDestinationField = 'riferimento' | 'citta' | 'cap' | 'indirizzo';
type DeliveryDestinationGeneralityField = 'indirizzo' | 'cap' | 'contatto';

@Component({
  selector: 'cus-customers-detail',
  imports: [
    MatBadgeModule, MatButtonModule, MatCardModule,
    MatTabsModule, TranslocoDirective, MatChipsModule, TranslocoCurrencyPipe, DatePipe, MatMenuModule,
    MatDividerModule, MatCheckboxModule, MatFormFieldModule, MatInputModule, LoadingSpinner
  ],
  templateUrl: './customers-detail.html',
  styleUrl: './customers-detail.scss',
})
export class CustomersDetail implements OnInit, OnDestroy {
  @ViewChild('notificationsOrigin') notificationsOrigin?: ElementRef<HTMLElement>;
  @ViewChild('notificationsPanel') notificationsPanel?: TemplateRef<unknown>;

  selectedTabIndex = 0;
  isDesktop = false;
  notesSelectionMode = false;
  customerId!: string;
  customerDetail: any;
  customerContacts: CustomerContact[] = [];
  customerNotes: CustomerNotes[] = [];
  deliveryDestinations: DeliveryDestinations[] = [];
  notificationJson: CustomerNotificationHeader = { Count: 0, Notifications: [] };
  customerDetailLoading = false;
  private addContactDialogRef?: MatDialogRef<CustomersAddContactDialogComponent>;
  private addressDetailsDialogRef?: MatDialogRef<CustomersAddressDetailsDialogComponent>;
  private editAddressDetailsDialogRef?: MatDialogRef<CustomersEditAddressDetailsDialogComponent>;
  private deliveryDestinationsDialogRef?: MatDialogRef<CustomersDeliveryDestinationsDialogComponent>;
  private noteBottomSheetRef?: MatBottomSheetRef<CustomersNoteBottomSheet>;
  private notificationsBottomSheetRef?: MatBottomSheetRef<CustomersNotificationsBottomSheet>;
  private noteDialogRef?: MatDialogRef<CustomersNoteDialogComponent>;
  private longPressTimeoutId?: ReturnType<typeof setTimeout>;
  private longPressTriggered = false;
  private readonly longPressDurationMs = 500;
  private notificationsOverlayRef?: OverlayRef;
  private breakpointObserver = inject(BreakpointObserver);
  private overlay = inject(Overlay);
  selectedContact?: CustomerContact;
  selectedNoteIds = new Set<string>();

  constructor(
    private dialog: MatDialog,
    private bottomSheet: MatBottomSheet,
    private host: ElementRef<HTMLElement>,
    private route: ActivatedRoute,
    private customerDetailService: CustomerDetailService,
    private viewContainerRef: ViewContainerRef,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      const routeId = params.get('customerId');

      if (!routeId) {
        console.error('ID cliente mancante in URL');
        return;
      }

      this.customerId = routeId;
      this.loadCustomerPage();
    });

    this.breakpointObserver
      .observe([`(min-width: ${Q8Breakpoints.desktop}px)`])
      .subscribe(result => {
        this.isDesktop = result.matches;
        if (this.isDesktop) {
          this.notificationsBottomSheetRef?.dismiss();
        } else {
          this.closeNotificationsPanel();
        }
      });
  }

  ngOnDestroy() {
    this.notificationsOverlayRef?.dispose();
  }

  // Ciclo campi anagrafica per semplificare
  readonly anagraficaFields = [
    { labelKey: 'anagrafica.fields.taxCode', key: 'codiceFiscale' },
    { labelKey: 'anagrafica.fields.atecoCode', key: 'codiceAteco' },
    { labelKey: 'anagrafica.fields.legalOffice', key: 'sedeLegale' },
    { labelKey: 'anagrafica.fields.operationalOffice', key: 'sedeOperativa' },
    { labelKey: 'anagrafica.fields.customerSince', key: 'clienteDal' },
  ];

  readonly destinoAttivoFields: { labelKey: string; key: ActiveDeliveryDestinationField }[] = [
      { labelKey: 'delivery.defaultAddress.fields.reference', key: 'riferimento' },
      { labelKey: 'delivery.defaultAddress.fields.city', key: 'citta' },
      { labelKey: 'delivery.defaultAddress.fields.postcode', key: 'cap' },
      { labelKey: 'delivery.defaultAddress.fields.address', key: 'indirizzo' },
    ];

  readonly deliveryDestinationGeneralitiesFields: { labelKey: string; key: DeliveryDestinationGeneralityField }[] = [
    { labelKey: 'delivery.destinations.generalities.address', key: 'indirizzo' },
    { labelKey: 'delivery.destinations.generalities.postcode', key: 'cap' },
    { labelKey: 'delivery.destinations.generalities.contact', key: 'contatto' },
  ];

  readonly deliveryDestinationNotesFields: { labelKey: string; key: keyof DeliveryDestinationNote }[] = [
    { labelKey: 'delivery.destinations.notes.freeNote', key: 'notaLibera' },
    { labelKey: 'delivery.destinations.notes.days', key: 'giorni' },
    { labelKey: 'delivery.destinations.notes.tailLift', key: 'sponda' },
  ];

  get sortedDeliveryDestinations(): DeliveryDestinations[] {
    return [...this.deliveryDestinations].sort((a, b) => Number(b.principale) - Number(a.principale));
  }

  get activeDeliveryDestination(): DeliveryDestinations | undefined {
    return this.deliveryDestinations.find(destination => destination.principale) ?? this.deliveryDestinations[0];
  }

  get receiptstatistics() {
    return this.customerDetail?.statistiche ?? this.customerDetail?.panoramica?.statistiche ?? [];
  }

  loadCustomerPage() {
    this.customerDetailLoading = true;
    this.customerDetail = {};
    this.customerContacts = [];
    this.customerNotes = [];
    this.deliveryDestinations = [];
    this.notificationJson = { Count: 0, Notifications: [] };
    this.selectedContact = undefined;
    this.exitNotesSelectionMode();

    // Usiamo il forkJoin in modo che faccia comparire tutto assieme una volta che le chiamate sono pronte
    forkJoin({
      detail: this.customerDetailService.loadCustomerDetail(this.customerId).pipe(
        catchError((err) => {
          console.error('Errore caricamento customer detail', err);
          //Se una delle chiamate fallisce, resistuisco un oggetto vuoto per non spaccare il layout
          return of(null);
        })
      ),
      contacts: this.customerDetailService.loadCustomerContacts(this.customerId).pipe(
        catchError((err) => {
          console.error('Errore caricamento contatti cliente', err);
          return of([] as CustomerContact[]);
        })
      ),
      notes: this.customerDetailService.loadCustomerNotes(this.customerId).pipe(
        catchError((err) => {
          console.error('Errore caricamento note cliente', err);
          return of([] as CustomerNotes[]);
        })
      ),
      destinations: this.customerDetailService.loadDeliveryDestinations(this.customerId).pipe(
        catchError((err) => {
          console.error('Errore caricamento destini di consegna', err);
          return of([] as DeliveryDestinations[]);
        })
      ),
    }).subscribe({
      next: (result) => {
        const detailResult = result.detail as CustomerDetailNotification | null;

        this.customerDetail = detailResult?.detail ?? {};
        this.notificationJson = detailResult?.notificationJson ?? { Count: 0, Notifications: [] };
        this.customerContacts = result.contacts;
        this.customerNotes = result.notes;
        this.deliveryDestinations = result.destinations;
        this.customerDetailLoading = false;
      },
      error: () => {
        this.customerDetailLoading = false;
      }
    });
  }

  private loadCustomerContacts() {
    this.customerDetailService.loadCustomerContacts(this.customerId).subscribe({
      next: (contacts: CustomerContact[]) => {
        this.customerContacts = contacts;
      },
      error: () => {
        this.customerContacts = [];
      }
    });
  }

  private loadCustomerNotes() {
    this.customerDetailService.loadCustomerNotes(this.customerId).subscribe({
      next: (notes: CustomerNotes[]) => {
        this.customerNotes = notes;
      },
      error: () => {
        this.customerNotes = [];
      }
    });
  }

  private loadDeliveryDestinations() {
    this.customerDetailService.loadDeliveryDestinations(this.customerId).subscribe({
      next: (destinations: DeliveryDestinations[]) => {
        this.deliveryDestinations = destinations;
      },
      error: () => {
        this.deliveryDestinations = [];
      },
    });
  }

  openAddContactDialog() {
    this.openContactDialog('add');
  }

  openEditContactDialog(contact: CustomerContact) {
    this.openContactDialog('edit', contact);
  }

  private openContactDialog(mode: 'add' | 'edit', contact?: CustomerContact) {
    if (this.addContactDialogRef) {
      return;
    }

    this.addContactDialogRef = this.dialog.open(CustomersAddContactDialogComponent, {
      data: {
        mode,
        contact,
      },
    });
    this.addContactDialogRef.afterClosed().subscribe((result?: {
      nomeCognome?: string;
      telefono?: string;
      cellulare?: string;
      fax?: string;
      email?: string;
      ruolo?: string;
      note?: string;
      default?: boolean;
    }) => {
      this.addContactDialogRef = undefined;

      if (!result) {
        return;
      }

      const payload = {
        Nominativo: result.nomeCognome ?? '',
        Ruolo: result.ruolo ?? '',
        Telefono: result.telefono ?? '',
        Cellulare: result.cellulare ?? '',
        Fax: result.fax ?? '',
        Email: result.email ?? '',
        Note: result.note ?? '',
      };

      const request$ = mode === 'edit' && contact
        ? this.customerDetailService.updateCustomerContact(this.customerId, contact.id, payload)
        : this.customerDetailService.addCustomerContact(this.customerId, payload);

      request$.subscribe({
        next: () => {
          this.loadCustomerContacts();
        },
        error: (err) => {
          console.error(`Errore ${mode === 'edit' ? 'modifica' : 'aggiunta'} contatto`, err);
        },
      });
    });
  }

  setSelectedContact(contact: CustomerContact) {
    this.selectedContact = contact;
  }

  openSelectedContactEmail() {
    const email = this.selectedContact?.email?.trim();

    if (!email) {
      return;
    }

    window.location.href = `mailto:${email}`;
  }

  openContactPhone(contact: CustomerContact) {
    const phone = (contact.cellulare || contact.telefono || '').trim();

    if (!phone) {
      return;
    }

    window.location.href = `tel:${phone}`;
  }

  deleteSelectedContact() {
    const contactId = this.selectedContact?.id;

    if (!contactId) {
      return;
    }

    this.customerDetailService.deleteCustomerContacts(this.customerId, [contactId]).subscribe({
      next: () => {
        this.selectedContact = undefined;
        this.loadCustomerContacts();
      },
      error: (err) => {
        console.error('Errore eliminazione contatto', err);
      },
    });
  }

  openDeliveryDestinationsDialog() {
    if (this.deliveryDestinationsDialogRef) {
      return;
    }

    this.deliveryDestinationsDialogRef = this.dialog.open(CustomersDeliveryDestinationsDialogComponent, {
      data: this.deliveryDestinations,
    });
    this.deliveryDestinationsDialogRef.afterClosed().subscribe((result?: DeliveryDestinationEditResult) => {
      this.deliveryDestinationsDialogRef = undefined;

      if (result?.action === 'edit') {
        this.updateDeliveryDestination(result.destination, result.value);
      }
    });
  }

  openAddressDetailsDialog(destination?: DeliveryDestinations) {
    if (this.addressDetailsDialogRef) {
      return;
    }

    const selectedDestination = destination ?? this.activeDeliveryDestination;

    if (!selectedDestination) {
      return;
    }

    this.addressDetailsDialogRef = this.dialog.open(CustomersAddressDetailsDialogComponent, {
      data: selectedDestination,
    });
    this.addressDetailsDialogRef.afterClosed().subscribe((result?: DeliveryDestinationEditResult) => {
      this.addressDetailsDialogRef = undefined;

      if (result?.action === 'edit') {
        this.updateDeliveryDestination(result.destination, result.value);
      }
    });
  }

  openEditAddressDetailsDialog(destination: DeliveryDestinations) {
    if (this.editAddressDetailsDialogRef) {
      return;
    }

    this.editAddressDetailsDialogRef = this.dialog.open(CustomersEditAddressDetailsDialogComponent, {
      data: destination,
    });
    this.editAddressDetailsDialogRef.afterClosed().subscribe((result?: DeliveryDestinationEditValue) => {
      this.editAddressDetailsDialogRef = undefined;

      if (result) {
        this.updateDeliveryDestination(destination, result);
      }
    });
  }

  setDefaultDeliveryDestination(destination: DeliveryDestinations) {
    if (destination.principale) {
      return;
    }

    this.updateDeliveryDestination(destination, undefined, true);
  }

  private updateDeliveryDestination(
    destination: DeliveryDestinations,
    changes?: DeliveryDestinationEditValue,
    principale = destination.principale,
  ) {
    const payload = this.buildDeliveryDestinationPayload(destination, changes, principale);

    this.customerDetailService.updateDeliveryDestination(this.customerId, destination.id, payload).subscribe({
      next: () => {
        this.loadDeliveryDestinations();
      },
      error: (err) => {
        console.error('Errore modifica destino di consegna', err);
      },
    });
  }

  private buildDeliveryDestinationPayload(
    destination: DeliveryDestinations,
    changes?: DeliveryDestinationEditValue,
    principale = destination.principale,
  ): UpdateDeliveryDestinationPayload {
    return {
      Nota: changes?.nota !== undefined ? changes.nota : destination.nota.notaLibera,
      Riferimento: changes?.riferimento !== undefined ? changes.riferimento : destination.riferimento,
      AddBolla: destination.nota.inBolla,
      Principale: principale,
      Orari: changes?.orari !== undefined ? changes.orari : destination.nota.orari,
      Contatto: changes?.contatto !== undefined ? changes.contatto : destination.nota.contatto,
      Giorni: changes?.giorni !== undefined ? changes.giorni : destination.nota.giorni,
      Sponda: changes?.sponda !== undefined ? changes.sponda : destination.nota.sponda,
    };
  }

  openNoteBottomSheet(note: CustomerNotes) {
    if (this.notesSelectionMode || this.longPressTriggered) {
      return;
    }

    if (this.noteBottomSheetRef) {
      return;
    }

    this.noteBottomSheetRef = this.bottomSheet.open(CustomersNoteBottomSheet, {
      data: note,
    });
    this.noteBottomSheetRef.afterDismissed().subscribe(result => {
      this.noteBottomSheetRef = undefined;

      if (result?.action === 'edit') {
        this.openNoteDialog('edit', note);
      }
    });
  }

  onNotificationsButtonClick() {
    if (this.isDesktop) {
      this.toggleNotificationsPanel();
      return;
    }

    this.openNotificationsBottomSheet();
  }

  openNotificationsBottomSheet() {
    if (this.notificationsBottomSheetRef) {
      return;
    }

    this.notificationsBottomSheetRef = this.bottomSheet.open(CustomersNotificationsBottomSheet, {
      ariaLabel: 'Notifiche',
      maxHeight: '480px',
      data: {
        notifications: this.notificationJson.Notifications ?? [],
      },
    });
    this.notificationsBottomSheetRef.afterDismissed().subscribe(() => {
      this.notificationsBottomSheetRef = undefined;
    });
  }

  toggleNotificationsPanel() {
    if (this.notificationsOverlayRef?.hasAttached()) {
      this.closeNotificationsPanel();
      return;
    }

    this.openNotificationsPanel();
  }

  closeNotificationsPanel() {
    this.notificationsOverlayRef?.detach();
  }

  getNotificationCardClass(severity: string) {
    const normalizedSeverity = severity?.toLowerCase?.() ?? '';

    switch (normalizedSeverity) {
      case 'danger':
      case 'error':
        return 'notification-card-danger';
      case 'warning':
      case 'warn':
      case 'alert':
        return 'notification-card-warning';
      case 'success':
        return 'notification-card-success';
      case 'info':
        return 'notification-card-info';
      default:
        return 'notification-card-default';
    }
  }

  getNotificationIconClass(severity: string) {
    const normalizedSeverity = severity?.toLowerCase?.() ?? '';

    switch (normalizedSeverity) {
      case 'danger':
      case 'error':
        return 'fa-solid fa-circle-exclamation';
      case 'success':
        return 'fa-solid fa-circle-check';
      case 'warning':
      case 'warn':
      case 'alert':
        return 'fa-solid fa-triangle-exclamation';
      case 'info':
        return 'fa-solid fa-circle-info';
      default:
        return 'fa-solid fa-circle-question';
    }
  }

  getNotificationIconColorClass(severity: string) {
    const normalizedSeverity = severity?.toLowerCase?.() ?? '';

    switch (normalizedSeverity) {
      case 'danger':
      case 'error':
        return 'notification-card-tone-danger';
      case 'success':
        return 'notification-card-tone-success';
      case 'warning':
      case 'warn':
      case 'alert':
        return 'notification-card-tone-warning';
      case 'info':
        return 'notification-card-tone-info';
      default:
        return 'notification-card-tone-default';
    }
  }

  getNotificationActionLabel(item: any) {
    return item?.ToAction || 'Pianifica visita';
  }

  openAddNoteDialog() {
    this.openNoteDialog('add');
  }

  openEditNoteDialog(note: CustomerNotes) {
    this.openNoteDialog('edit', note);
  }

  private openNoteDialog(mode: 'add' | 'edit' | 'view', note?: CustomerNotes) {
    if (this.noteDialogRef) {
      return;
    }

    this.noteDialogRef = this.dialog.open(CustomersNoteDialogComponent, {
      data: { mode, note },
    });
    this.noteDialogRef.afterClosed().subscribe(result => {
      this.noteDialogRef = undefined;

      if (result?.action === 'save' && mode !== 'view') {
        const formValue = result.value as {
          data?: Date | string;
          titolo?: string;
          testo?: string;
          addOrdine?: boolean;
        };
        const payload = {
          Titolo: formValue.titolo ?? '',
          Testo: formValue.testo ?? '',
          Data: this.formatNoteDateForApi(formValue.data),
          addOrdine: formValue.addOrdine ?? false,
        };
        const request$ = mode === 'edit' && note
          ? this.customerDetailService.updateCustomerNote(this.customerId, note.id, payload)
          : this.customerDetailService.addCustomerNote(this.customerId, payload);

        request$.subscribe({
          next: () => {
            this.loadCustomerNotes();
          },
          error: (err) => {
            console.error(`Errore ${mode === 'edit' ? 'modifica' : 'aggiunta'} nota`, err);
          },
        });
      }

      if (result?.action === 'edit' && note) {
        this.openNoteDialog('edit', note);
      }
    });
  }

  onNoteCardClick(note: CustomerNotes) {
    if (this.longPressTriggered) {
      this.longPressTriggered = false;
      return;
    }

    if (this.notesSelectionMode) {
      this.toggleNoteSelection(note.id);
      return;
    }

    if (this.isDesktop) {
      this.openNoteDialog('view', note);
      return;
    }

    this.openNoteBottomSheet(note);
  }

  onNoteCardKeydown(note: CustomerNotes, event: KeyboardEvent) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.onNoteCardClick(note);
  }

  startNoteLongPress(note: CustomerNotes) {
    this.clearNoteLongPress();
    this.longPressTriggered = false;
    this.longPressTimeoutId = setTimeout(() => {
      this.longPressTriggered = true;
      this.enterNotesSelectionMode(note.id);
    }, this.longPressDurationMs);
  }

  clearNoteLongPress() {
    if (this.longPressTimeoutId) {
      clearTimeout(this.longPressTimeoutId);
      this.longPressTimeoutId = undefined;
    }
  }

  enterNotesSelectionMode(noteId: string) {
    this.notesSelectionMode = true;
    this.selectedNoteIds.add(noteId);
    this.selectedNoteIds = new Set(this.selectedNoteIds);
  }

  exitNotesSelectionMode() {
    this.notesSelectionMode = false;
    this.selectedNoteIds.clear();
    this.selectedNoteIds = new Set(this.selectedNoteIds);
  }

  toggleNoteSelection(noteId: string) {
    if (this.selectedNoteIds.has(noteId)) {
      this.selectedNoteIds.delete(noteId);
    } else {
      this.selectedNoteIds.add(noteId);
    }

    this.selectedNoteIds = new Set(this.selectedNoteIds);
  }

  isNoteSelected(noteId: string) {
    return this.selectedNoteIds.has(noteId);
  }

  deleteSelectedNotes() {
    const noteIds = Array.from(this.selectedNoteIds);

    if (!noteIds.length) {
      return;
    }

    this.customerDetailService.deleteCustomerNotes(this.customerId, noteIds).subscribe({
      next: () => {
        this.exitNotesSelectionMode();
        this.loadCustomerNotes();
      },
      error: (err) => {
        console.error('Errore eliminazione note', err);
      },
    });
  }

  private formatNoteDateForApi(value?: Date | string): string {
    if (!value) {
      return '';
    }

    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  getAnagraficaValue(key: string): string | number {
    if (key === 'codiceAteco' && this.customerDetail?.codiceAteco) {
      const { codice, nome } = this.customerDetail.codiceAteco;
      return [codice, nome].filter(Boolean).join(' - ') || '-';
    }

    return this.customerDetail?.[key] ?? '-';
  }

  getDestinoAttivoValue(key: ActiveDeliveryDestinationField): string | number | boolean {
    return this.activeDeliveryDestination?.[key] ?? '-';
  }

  getDeliveryDestinationGeneralityValue(
    destination: DeliveryDestinations,
    key: DeliveryDestinationGeneralityField,
  ): string | number {
    if (key === 'contatto') {
      return destination.nota.contatto ?? '-';
    }

    return destination[key] || '-';
  }

  // Workaround per far funzionare scroll e focus sull'header delle mat tab
  onTabIndexChange(index: number) {
    this.selectedTabIndex = index;

    this.scrollActiveTabIntoView();

    requestAnimationFrame(() => {
      this.scrollActiveTabIntoView();
    });
  }
  scrollActiveTabIntoView() {
    const tabContainer = this.host.nativeElement.querySelector('.tabs .mat-mdc-tab-label-container') as HTMLElement | null;
    const activeTab = this.host.nativeElement.querySelector('.tabs .mat-mdc-tab.mdc-tab--active') as HTMLElement | null;

    if (!tabContainer || !activeTab) {
      return;
    }

    const containerLeft = tabContainer.scrollLeft;
    const containerRight = containerLeft + tabContainer.clientWidth;
    const tabLeft = activeTab.offsetLeft;
    const tabRight = tabLeft + activeTab.offsetWidth;

    if (tabLeft < containerLeft) {
      tabContainer.scrollLeft = tabLeft;
      return;
    }

    if (tabRight > containerRight) {
      tabContainer.scrollLeft = tabRight - tabContainer.clientWidth;
    }
  }

  openNotificationsPanel() {
    if (!this.notificationsOrigin || !this.notificationsPanel) {
      return;
    }

    if (!this.notificationsOverlayRef) {
      this.createNotificationsOverlay();
    }

    if (!this.notificationsOverlayRef?.hasAttached()) {
      this.notificationsOverlayRef?.attach(
        new TemplatePortal(this.notificationsPanel, this.viewContainerRef)
      );
    }
  }

  createNotificationsOverlay() {
    this.notificationsOverlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .global()
        .top('0')
        .right('0'),
    });

    this.notificationsOverlayRef.backdropClick().subscribe(() => {
      this.closeNotificationsPanel();
    });

    this.notificationsOverlayRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.closeNotificationsPanel();
      }
    });
  }
}
